// src/constants/transportation.ts
var TRANSPORTATION_ERROR_CODES = {
  TRANSPORT_PICKUP_CHANGED: "TRANSPORT_PICKUP_CHANGED",
  TRANSPORT_CARRIER_CONFLICT: "TRANSPORT_CARRIER_CONFLICT",
  NO_COMMON_CARRIER: "NO_COMMON_CARRIER",
  /** @deprecated Accept only for backward-compatible client rendering. */
  TRANSPORT_NO_COMMON_CARRIER: "TRANSPORT_NO_COMMON_CARRIER",
  TRANSPORT_MODE_MISMATCH: "TRANSPORT_MODE_MISMATCH",
  TRANSPORT_OPTIONS_STALE: "TRANSPORT_OPTIONS_STALE",
  TRANSPORT_CONFIRMATION_IN_PROGRESS: "TRANSPORT_CONFIRMATION_IN_PROGRESS",
  TRANSPORT_CONFIRMATION_UNKNOWN: "TRANSPORT_CONFIRMATION_UNKNOWN",
  TRANSPORT_GENERATION_IN_PROGRESS: "TRANSPORT_GENERATION_IN_PROGRESS",
  TRANSPORT_GENERATION_UNKNOWN: "TRANSPORT_GENERATION_UNKNOWN",
  TRANSPORT_GENERATION_STALE: "TRANSPORT_GENERATION_STALE",
  IMPORTED_SHIPMENT_READ_ONLY: "IMPORTED_SHIPMENT_READ_ONLY"
};

// src/constants/permissions.ts
var PERMISSIONS = {
  ACCOUNT_USERS_MANAGE: "account:users:manage",
  ACCOUNT_ROLES_MANAGE: "account:roles:manage",
  STORES_CONNECT: "stores:connect",
  STORES_SETTINGS_UPDATE: "stores:settings:update",
  AMAZON_REAUTHORIZE: "amazon:reauthorize",
  SYNC_RUN: "sync:run",
  DASHBOARD_VIEW: "dashboard:view",
  ANALYTICS_VIEW: "analytics:view",
  REPORTS_VIEW: "reports:view",
  INVENTORY_VIEW: "inventory:view",
  INVENTORY_UPDATE: "inventory:update",
  RESTOCK_VIEW: "restock:view",
  RESTOCK_MANAGE: "restock:manage",
  PRODUCTS_VIEW: "products:view",
  PRODUCTS_MANAGE: "products:manage",
  REPRICER_MANAGE: "repricer:manage",
  SUPPLIERS_VIEW: "suppliers:view",
  SUPPLIERS_MANAGE: "suppliers:manage",
  PURCHASE_ORDERS_VIEW: "purchase_orders:view",
  PURCHASE_ORDERS_MANAGE: "purchase_orders:manage",
  MASTER_SKU_ALLOCATIONS_REVIEW: "master_sku_allocations:review",
  SHIPMENTS_VIEW: "shipments:view",
  SHIPMENTS_CREATE: "shipments:create",
  SHIPMENTS_MANAGE: "shipments:manage",
  SHIPMENTS_FINALIZE: "shipments:finalize",
  LABELS_PRINT: "labels:print",
  SHIPPING_MANAGE: "shipping:manage",
  ALERTS_VIEW: "alerts:view",
  ALERTS_MANAGE: "alerts:manage",
  READINESS_VIEW: "readiness:view",
  RESEARCH_VIEW: "research:view",
  RESEARCH_MANAGE: "research:manage",
  RESEARCH_ENRICH: "research:enrich",
  SOLICITATIONS_VIEW: "solicitations:view",
  SOLICITATIONS_MANAGE: "solicitations:manage",
  REVIEWS_VIEW: "reviews:view",
  AI_USE: "ai:use",
  WALKIE_TALKIE_USE: "walkie_talkie:use",
  WALKIE_TALKIE_DIAL: "walkie_talkie:dial",
  CARRIER_CONFIG_MANAGE: "carrier_config:manage"
};
var ALL_PERMISSIONS = Object.values(PERMISSIONS);
var PERMISSION_DETAILS = {
  [PERMISSIONS.DASHBOARD_VIEW]: {
    label: "Dashboard / View",
    description: "See account dashboard metrics and widgets."
  },
  [PERMISSIONS.AI_USE]: {
    label: "Zeno / Use",
    description: "Use the Zeno AI assistant for allowed stores."
  },
  [PERMISSIONS.WALKIE_TALKIE_USE]: {
    label: "Walkie Talkie / Use",
    description: "Appear in the account walkie-talkie roster and receive push-to-talk audio."
  },
  [PERMISSIONS.WALKIE_TALKIE_DIAL]: {
    label: "Walkie Talkie / Dial",
    description: "Start push-to-talk sessions with available account members."
  },
  [PERMISSIONS.REPRICER_MANAGE]: {
    label: "Repricer / Manage",
    description: "Show Repricer and manage repricing setup, profiles, and live pricing workflows."
  },
  [PERMISSIONS.PRODUCTS_VIEW]: {
    label: "Products / View",
    description: "See product catalog, listing, and SKU information."
  },
  [PERMISSIONS.PRODUCTS_MANAGE]: {
    label: "Products / Manage",
    description: "Edit product details, costs, supplier assignments, and product setup."
  },
  [PERMISSIONS.INVENTORY_VIEW]: {
    label: "Inventory / View",
    description: "See product inventory levels, stock quantities, and stock health. Does not show Restock Suggestions."
  },
  [PERMISSIONS.INVENTORY_UPDATE]: {
    label: "Inventory / Update",
    description: "Update local inventory quantities and inventory fields."
  },
  [PERMISSIONS.RESTOCK_VIEW]: {
    label: "Restock / View Suggestions",
    description: "Show Restock Suggestions, restock alerts, and restock planning dashboards."
  },
  [PERMISSIONS.RESTOCK_MANAGE]: {
    label: "Restock / Manage Planning",
    description: "Update restock flags, alert dates, notes, inactive status, and planning fields."
  },
  [PERMISSIONS.SUPPLIERS_VIEW]: {
    label: "Suppliers / View",
    description: "See supplier records and related product sourcing data."
  },
  [PERMISSIONS.SUPPLIERS_MANAGE]: {
    label: "Suppliers / Manage",
    description: "Create and edit supplier records and assignments."
  },
  [PERMISSIONS.PURCHASE_ORDERS_VIEW]: {
    label: "Purchase Orders / View",
    description: "See purchase orders and receiving history."
  },
  [PERMISSIONS.PURCHASE_ORDERS_MANAGE]: {
    label: "Purchase Orders / Manage",
    description: "Create, update, and receive purchase orders."
  },
  [PERMISSIONS.MASTER_SKU_ALLOCATIONS_REVIEW]: {
    label: "Master SKU Allocations / Review",
    description: "Review prorated or manually edited Master SKU allocation proposals and authorize unbuild reversals."
  },
  [PERMISSIONS.SHIPMENTS_VIEW]: {
    label: "Shipments / View",
    description: "See shipment drafts, working shipments, and shipment history."
  },
  [PERMISSIONS.SHIPMENTS_CREATE]: {
    label: "Shipments / Create",
    description: "Create new shipment drafts and workflow plans."
  },
  [PERMISSIONS.SHIPMENTS_MANAGE]: {
    label: "Shipments / Manage",
    description: "Edit shipment contents, boxes, carrier choices, and workflow state."
  },
  [PERMISSIONS.SHIPMENTS_FINALIZE]: {
    label: "Shipments / Finalize",
    description: "Finalize shipments and submit finalization actions."
  },
  [PERMISSIONS.LABELS_PRINT]: {
    label: "Labels / Print",
    description: "Print shipment, box, and product labels."
  },
  [PERMISSIONS.SHIPPING_MANAGE]: {
    label: "Shipping / Manage",
    description: "Manage shipping options, ship-from addresses, and rate workflows."
  },
  [PERMISSIONS.CARRIER_CONFIG_MANAGE]: {
    label: "Carrier Config / Manage",
    description: "Manage carrier credentials and carrier account settings."
  },
  [PERMISSIONS.SOLICITATIONS_VIEW]: {
    label: "Solicitations / View",
    description: "See review and seller feedback solicitation campaigns."
  },
  [PERMISSIONS.SOLICITATIONS_MANAGE]: {
    label: "Solicitations / Manage",
    description: "Create and manage solicitation campaigns."
  },
  [PERMISSIONS.REVIEWS_VIEW]: {
    label: "Reviews / View",
    description: "See review monitoring and feedback signals."
  },
  [PERMISSIONS.ALERTS_VIEW]: {
    label: "Alerts / View",
    description: "See alerts and the alert center."
  },
  [PERMISSIONS.ALERTS_MANAGE]: {
    label: "Alerts / Manage",
    description: "Manage alert state and alert settings."
  },
  [PERMISSIONS.RESEARCH_VIEW]: {
    label: "Research / View",
    description: "See product research lists and scanner results."
  },
  [PERMISSIONS.RESEARCH_MANAGE]: {
    label: "Research / Manage",
    description: "Create, upload, and manage product research work."
  },
  [PERMISSIONS.RESEARCH_ENRICH]: {
    label: "Research / Use Enrichment",
    description: "Use the account product-research allowance for uploads, scans, retries, and rescans."
  },
  [PERMISSIONS.ANALYTICS_VIEW]: {
    label: "Analytics / View",
    description: "See analytics pages and SKU-level analytics."
  },
  [PERMISSIONS.REPORTS_VIEW]: {
    label: "Reports / View",
    description: "See saved reports and reporting views."
  },
  [PERMISSIONS.READINESS_VIEW]: {
    label: "Operational Readiness / View",
    description: "Show Operational Readiness nav, dashboard readiness widgets, and setup checks."
  },
  [PERMISSIONS.ACCOUNT_USERS_MANAGE]: {
    label: "Team / Manage Users",
    description: "Invite, remove, suspend, and update account members."
  },
  [PERMISSIONS.ACCOUNT_ROLES_MANAGE]: {
    label: "Roles / Manage",
    description: "Create roles and edit permissions for assigned members."
  },
  [PERMISSIONS.STORES_CONNECT]: {
    label: "Stores / Connect",
    description: "Connect new Amazon stores to the account."
  },
  [PERMISSIONS.STORES_SETTINGS_UPDATE]: {
    label: "Store Settings / Update",
    description: "Edit store settings, business details, and store-level preferences."
  },
  [PERMISSIONS.AMAZON_REAUTHORIZE]: {
    label: "Amazon / Reauthorize",
    description: "Reconnect Amazon authorization when SP-API access needs refreshing."
  },
  [PERMISSIONS.SYNC_RUN]: {
    label: "Sync / Run",
    description: "Start sync, reconciliation, and product-image refresh jobs."
  }
};
var PERMISSION_GROUPS = [
  {
    id: "dashboard",
    label: "Dashboard",
    permissions: [PERMISSIONS.DASHBOARD_VIEW]
  },
  {
    id: "zeno",
    label: "Zeno",
    permissions: [PERMISSIONS.AI_USE]
  },
  {
    id: "walkie-talkie",
    label: "Walkie Talkie",
    permissions: [
      PERMISSIONS.WALKIE_TALKIE_USE,
      PERMISSIONS.WALKIE_TALKIE_DIAL
    ]
  },
  {
    id: "repricer",
    label: "Repricer",
    permissions: [PERMISSIONS.REPRICER_MANAGE]
  },
  {
    id: "inventory",
    label: "Inventory",
    permissions: [
      PERMISSIONS.PRODUCTS_VIEW,
      PERMISSIONS.PRODUCTS_MANAGE,
      PERMISSIONS.INVENTORY_VIEW,
      PERMISSIONS.INVENTORY_UPDATE,
      PERMISSIONS.SUPPLIERS_VIEW,
      PERMISSIONS.SUPPLIERS_MANAGE,
      PERMISSIONS.PURCHASE_ORDERS_VIEW,
      PERMISSIONS.PURCHASE_ORDERS_MANAGE,
      PERMISSIONS.MASTER_SKU_ALLOCATIONS_REVIEW
    ]
  },
  {
    id: "restock",
    label: "Restock",
    permissions: [
      PERMISSIONS.RESTOCK_VIEW,
      PERMISSIONS.RESTOCK_MANAGE
    ]
  },
  {
    id: "shipping",
    label: "Shipping",
    permissions: [
      PERMISSIONS.SHIPMENTS_VIEW,
      PERMISSIONS.SHIPMENTS_CREATE,
      PERMISSIONS.SHIPMENTS_MANAGE,
      PERMISSIONS.SHIPMENTS_FINALIZE,
      PERMISSIONS.LABELS_PRINT,
      PERMISSIONS.SHIPPING_MANAGE,
      PERMISSIONS.CARRIER_CONFIG_MANAGE
    ]
  },
  {
    id: "reviews",
    label: "Reviews",
    permissions: [
      PERMISSIONS.SOLICITATIONS_VIEW,
      PERMISSIONS.SOLICITATIONS_MANAGE,
      PERMISSIONS.REVIEWS_VIEW,
      PERMISSIONS.ALERTS_VIEW,
      PERMISSIONS.ALERTS_MANAGE
    ]
  },
  {
    id: "research",
    label: "Research",
    permissions: [
      PERMISSIONS.RESEARCH_VIEW,
      PERMISSIONS.RESEARCH_MANAGE,
      PERMISSIONS.RESEARCH_ENRICH
    ]
  },
  {
    id: "intelligence",
    label: "Intelligence",
    permissions: [
      PERMISSIONS.ANALYTICS_VIEW,
      PERMISSIONS.REPORTS_VIEW
    ]
  },
  {
    id: "readiness",
    label: "Operational Readiness",
    permissions: [PERMISSIONS.READINESS_VIEW]
  },
  {
    id: "settings",
    label: "Settings",
    permissions: [
      PERMISSIONS.ACCOUNT_USERS_MANAGE,
      PERMISSIONS.ACCOUNT_ROLES_MANAGE,
      PERMISSIONS.STORES_CONNECT,
      PERMISSIONS.STORES_SETTINGS_UPDATE,
      PERMISSIONS.AMAZON_REAUTHORIZE,
      PERMISSIONS.SYNC_RUN
    ]
  }
];
var ROLE_SLUGS = {
  OWNER: "owner",
  ADMIN: "admin",
  WAREHOUSE: "warehouse",
  PURCHASING: "purchasing",
  ANALYST: "analyst"
};
var PRESET_ROLE_DEFINITIONS = [
  {
    slug: ROLE_SLUGS.OWNER,
    name: "Owner",
    description: "Full account ownership and cannot be removed from the last owner seat.",
    permissions: ALL_PERMISSIONS,
    isOwnerRole: true
  },
  {
    slug: ROLE_SLUGS.ADMIN,
    name: "Admin",
    description: "Full operational access except immutable owner controls.",
    permissions: ALL_PERMISSIONS.filter((permission) => permission !== PERMISSIONS.ACCOUNT_ROLES_MANAGE)
  },
  {
    slug: ROLE_SLUGS.WAREHOUSE,
    name: "Warehouse",
    description: "Warehouse floor access for packing, labels, shipments, and basic inventory/product visibility.",
    permissions: [
      PERMISSIONS.READINESS_VIEW,
      PERMISSIONS.INVENTORY_VIEW,
      PERMISSIONS.PRODUCTS_VIEW,
      PERMISSIONS.SHIPMENTS_VIEW,
      PERMISSIONS.SHIPMENTS_CREATE,
      PERMISSIONS.SHIPMENTS_MANAGE,
      PERMISSIONS.SHIPMENTS_FINALIZE,
      PERMISSIONS.LABELS_PRINT,
      PERMISSIONS.SHIPPING_MANAGE,
      PERMISSIONS.ALERTS_VIEW,
      PERMISSIONS.WALKIE_TALKIE_USE,
      PERMISSIONS.WALKIE_TALKIE_DIAL
    ]
  },
  {
    slug: ROLE_SLUGS.PURCHASING,
    name: "Purchasing",
    description: "Purchasing, suppliers, restock, product management, and purchase orders.",
    permissions: [
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.READINESS_VIEW,
      PERMISSIONS.REPRICER_MANAGE,
      PERMISSIONS.RESTOCK_VIEW,
      PERMISSIONS.RESTOCK_MANAGE,
      PERMISSIONS.INVENTORY_VIEW,
      PERMISSIONS.INVENTORY_UPDATE,
      PERMISSIONS.PRODUCTS_VIEW,
      PERMISSIONS.PRODUCTS_MANAGE,
      PERMISSIONS.SUPPLIERS_VIEW,
      PERMISSIONS.SUPPLIERS_MANAGE,
      PERMISSIONS.PURCHASE_ORDERS_VIEW,
      PERMISSIONS.PURCHASE_ORDERS_MANAGE,
      PERMISSIONS.MASTER_SKU_ALLOCATIONS_REVIEW,
      PERMISSIONS.ALERTS_VIEW,
      PERMISSIONS.WALKIE_TALKIE_USE,
      PERMISSIONS.WALKIE_TALKIE_DIAL
    ]
  },
  {
    slug: ROLE_SLUGS.ANALYST,
    name: "Analyst",
    description: "Read-only dashboard, analytics, reports, inventory, and product access.",
    permissions: [
      PERMISSIONS.DASHBOARD_VIEW,
      PERMISSIONS.READINESS_VIEW,
      PERMISSIONS.ANALYTICS_VIEW,
      PERMISSIONS.REPORTS_VIEW,
      PERMISSIONS.RESTOCK_VIEW,
      PERMISSIONS.INVENTORY_VIEW,
      PERMISSIONS.PRODUCTS_VIEW,
      PERMISSIONS.SUPPLIERS_VIEW,
      PERMISSIONS.PURCHASE_ORDERS_VIEW,
      PERMISSIONS.SHIPMENTS_VIEW,
      PERMISSIONS.ALERTS_VIEW,
      PERMISSIONS.RESEARCH_VIEW,
      PERMISSIONS.REVIEWS_VIEW,
      PERMISSIONS.WALKIE_TALKIE_USE,
      PERMISSIONS.WALKIE_TALKIE_DIAL
    ]
  }
];
function isPermission(value) {
  return ALL_PERMISSIONS.includes(value);
}

// src/constants/dashboardWidgets.ts
var DASHBOARD_WIDGET_IDS = [
  "kpi-active-skus",
  "kpi-fba-units",
  "kpi-inbound",
  "kpi-on-order",
  "kpi-revenue",
  "kpi-profit",
  "kpi-roi",
  "inventory-overview",
  "sales",
  "revenue-chart",
  "financial-summary",
  "top-products",
  "inventory-health",
  "next-actions",
  "restock-alerts",
  "purchase-orders",
  "supplier-restock",
  "restock-list",
  "setup-focus",
  "system-alerts"
];
var DASHBOARD_WIDGET_PERMISSION_REQUIREMENTS = {
  "kpi-active-skus": [PERMISSIONS.INVENTORY_VIEW],
  "kpi-fba-units": [PERMISSIONS.INVENTORY_VIEW],
  "kpi-inbound": [PERMISSIONS.INVENTORY_VIEW],
  "kpi-on-order": [PERMISSIONS.PURCHASE_ORDERS_VIEW],
  "kpi-revenue": [PERMISSIONS.ANALYTICS_VIEW],
  "kpi-profit": [PERMISSIONS.ANALYTICS_VIEW],
  "kpi-roi": [PERMISSIONS.ANALYTICS_VIEW],
  "inventory-overview": [PERMISSIONS.INVENTORY_VIEW],
  sales: [PERMISSIONS.ANALYTICS_VIEW],
  "revenue-chart": [PERMISSIONS.ANALYTICS_VIEW],
  "financial-summary": [PERMISSIONS.ANALYTICS_VIEW],
  "top-products": [PERMISSIONS.ANALYTICS_VIEW],
  "inventory-health": [PERMISSIONS.INVENTORY_VIEW],
  "next-actions": [],
  "restock-alerts": [PERMISSIONS.RESTOCK_VIEW],
  "purchase-orders": [PERMISSIONS.PURCHASE_ORDERS_VIEW],
  "supplier-restock": [PERMISSIONS.RESTOCK_VIEW, PERMISSIONS.SUPPLIERS_VIEW],
  "restock-list": [PERMISSIONS.RESTOCK_VIEW],
  "setup-focus": [PERMISSIONS.READINESS_VIEW],
  "system-alerts": [PERMISSIONS.ALERTS_VIEW]
};
function canAccessDashboardWidget(widgetId, permissions) {
  const permissionSet = new Set(permissions);
  if (widgetId === "next-actions") {
    return permissionSet.has(PERMISSIONS.RESTOCK_VIEW) || permissionSet.has(PERMISSIONS.READINESS_VIEW);
  }
  return DASHBOARD_WIDGET_PERMISSION_REQUIREMENTS[widgetId].every((permission) => permissionSet.has(permission));
}
function getAvailableDashboardWidgets(permissions) {
  return DASHBOARD_WIDGET_IDS.filter((widgetId) => canAccessDashboardWidget(widgetId, permissions));
}

// src/constants/dashboardContract.ts
var DASHBOARD_SCHEMA_VERSION = 3;

// src/constants/zeno.ts
var ZENO_CAROUSEL_SUGGESTIONS = [
  { id: "store_revenue_7d", prompt: "What is my store revenue for the last 7 days?" },
  { id: "top_asins_revenue_7d", prompt: "Top 5 ASINs by revenue for 7 days" },
  { id: "products_needing_attention", prompt: "Which products need attention right now?" },
  { id: "recent_order_movement", prompt: "Summarize recent order movement" },
  { id: "shipment_work_in_progress", prompt: "What shipment work is currently in progress?" },
  { id: "stockout_risk", prompt: "Which products are at risk of stocking out?" },
  { id: "inventory_attention", prompt: "Show inventory attention items" },
  { id: "top_asins_by_units", prompt: "Which ASINs are my top sellers by units?" },
  { id: "portfolio_demand_forecast", prompt: "Forecast demand across my portfolio" }
];

// src/constants/restock.ts
var RESTOCK_STATUS = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  WATCH: "WATCH",
  IGNORE: "IGNORE"
};
var RESTOCK_STATUS_LABELS = {
  ACTIVE: "Active",
  INACTIVE: "Inactive",
  WATCH: "Watch",
  IGNORE: "Ignore"
};
var RESTOCK_STATUS_COLORS = {
  ACTIVE: { bg: "bg-green-500/10", text: "text-green-500", icon: "\u2713" },
  INACTIVE: { bg: "bg-gray-500/10", text: "text-gray-500", icon: "\u25CB" },
  WATCH: { bg: "bg-blue-500/10", text: "text-blue-500", icon: "\u{1F441}\uFE0F" },
  IGNORE: { bg: "bg-red-500/10", text: "text-red-500", icon: "\u2298" }
};
var RESTOCK_FILTER_CATEGORIES = {
  FLAG: "flag",
  STATUS: "status",
  SUPPLIER: "supplier",
  VELOCITY: "velocity",
  ORDERS: "orders",
  INVENTORY: "inventory",
  COST: "cost",
  PRICE: "price",
  LOGISTICS: "logistics",
  SEARCH: "search"
};
var RESTOCK_FILTERS = {
  // Flag Filters
  flag: {
    id: "flag",
    label: "Flag",
    type: "multiselect",
    options: [
      { value: "RED", label: "Red" },
      { value: "ORANGE", label: "Orange" },
      { value: "YELLOW", label: "Yellow" },
      { value: "PURPLE", label: "Purple" },
      { value: "BLUE", label: "Blue" },
      { value: "NONE", label: "No Flag" }
    ]
  },
  // Status Filters
  status: {
    id: "status",
    label: "Status",
    type: "multiselect",
    options: [
      { value: "ACTIVE", label: "Active" },
      { value: "INACTIVE", label: "Inactive" },
      { value: "WATCH", label: "Watch" },
      { value: "IGNORE", label: "Ignore" }
    ]
  },
  // Supplier Filter (populated dynamically from database)
  supplier: {
    id: "supplier",
    label: "Supplier",
    type: "multiselect",
    options: []
    // Populated from API
  },
  // Velocity Filters (range)
  velocity2d: {
    id: "velocity2d",
    label: "2-Day Velocity",
    type: "range",
    min: 0,
    max: 1e3
  },
  velocity7d: {
    id: "velocity7d",
    label: "7-Day Velocity",
    type: "range",
    min: 0,
    max: 1e3
  },
  velocity30d: {
    id: "velocity30d",
    label: "30-Day Velocity",
    type: "range",
    min: 0,
    max: 1e3
  },
  velocity60d: {
    id: "velocity60d",
    label: "60-Day Velocity",
    type: "range",
    min: 0,
    max: 1e3
  },
  velocity90d: {
    id: "velocity90d",
    label: "90-Day Velocity",
    type: "range",
    min: 0,
    max: 1e3
  },
  velocity180d: {
    id: "velocity180d",
    label: "180-Day Velocity",
    type: "range",
    min: 0,
    max: 1e3
  },
  // Order Count Filters (range)
  orders2d: {
    id: "orders2d",
    label: "2-Day Orders",
    type: "range",
    min: 0,
    max: 1e4
  },
  orders7d: {
    id: "orders7d",
    label: "7-Day Orders",
    type: "range",
    min: 0,
    max: 1e4
  },
  orders30d: {
    id: "orders30d",
    label: "30-Day Orders",
    type: "range",
    min: 0,
    max: 1e4
  },
  orders60d: {
    id: "orders60d",
    label: "60-Day Orders",
    type: "range",
    min: 0,
    max: 1e4
  },
  orders90d: {
    id: "orders90d",
    label: "90-Day Orders",
    type: "range",
    min: 0,
    max: 1e4
  },
  orders180d: {
    id: "orders180d",
    label: "180-Day Orders",
    type: "range",
    min: 0,
    max: 1e4
  },
  // Inventory Filters (range)
  fbaInventory: {
    id: "fbaInventory",
    label: "FBA Inventory",
    type: "range",
    min: 0,
    max: 1e5
  },
  localQty: {
    id: "localQty",
    label: "Local Quantity",
    type: "range",
    min: 0,
    max: 1e5
  },
  inboundQty: {
    id: "inboundQty",
    label: "Inbound Quantity",
    type: "range",
    min: 0,
    max: 1e5
  },
  daysOfStock: {
    id: "daysOfStock",
    label: "Days of Stock",
    type: "range",
    min: 0,
    max: 365
  },
  // Cost Filters (range)
  supplierCost: {
    id: "supplierCost",
    label: "Supplier Cost",
    type: "range",
    min: 0,
    max: 1e4
  },
  inboundShippingCost: {
    id: "inboundShippingCost",
    label: "Inbound Shipping Cost",
    type: "range",
    min: 0,
    max: 1e3
  },
  // Price Filters (range)
  currentPrice: {
    id: "currentPrice",
    label: "Current Price",
    type: "range",
    min: 0,
    max: 1e4
  },
  avg7dPrice: {
    id: "avg7dPrice",
    label: "Avg 7D Price",
    type: "range",
    min: 0,
    max: 1e4
  },
  // Financial Metrics (range)
  estimatedMonthly: {
    id: "estimatedMonthly",
    label: "Estimated Monthly",
    type: "range",
    min: 0,
    max: 1e5
  },
  markupPercent: {
    id: "markupPercent",
    label: "Markup %",
    type: "range",
    min: 0,
    max: 1e3
  },
  // Logistics Filters (boolean)
  hazmat: {
    id: "hazmat",
    label: "Hazmat",
    type: "boolean"
  },
  oversized: {
    id: "oversized",
    label: "Oversized",
    type: "boolean"
  },
  // Search Filter
  search: {
    id: "search",
    label: "Search",
    type: "search",
    placeholder: "Search by SKU, Title, or ASIN..."
  }
};
var RESTOCK_COLUMN_GROUPS = [
  {
    id: "selection",
    label: "Selection",
    columns: ["select"],
    defaultVisible: true
  },
  {
    id: "image",
    label: "Image",
    columns: ["image"],
    defaultVisible: true
  },
  {
    id: "product",
    label: "Product (SKU, Title, ASIN)",
    columns: ["product"],
    defaultVisible: true
  },
  {
    id: "identifiers",
    label: "FNSKU, UPC, EAN",
    columns: ["identifiers"],
    defaultVisible: false
  },
  {
    id: "workflow",
    label: "Flag, Alert Date, Status",
    columns: ["workflow"],
    defaultVisible: true
  },
  {
    id: "supplier",
    label: "Supplier Info",
    columns: ["supplier"],
    defaultVisible: true
  },
  {
    id: "inventory_summary",
    label: "Inventory (QoH, Inbound, Reserved)",
    columns: ["inventory_summary"],
    defaultVisible: true
  },
  {
    id: "restock",
    label: "Restock Metrics",
    columns: ["restock"],
    defaultVisible: true
  },
  {
    id: "decision_support",
    label: "Recommendation Reasons",
    columns: ["decision_support"],
    defaultVisible: true
  },
  {
    id: "velocity_short",
    label: "Velocity: 2D, 7D, 30D",
    columns: ["velocity_short"],
    defaultVisible: true
  },
  {
    id: "velocity_long",
    label: "Velocity: 60D, 90D, 180D",
    columns: ["velocity_long"],
    defaultVisible: false
  },
  {
    id: "orders_short",
    label: "Orders: 2D, 7D, 30D",
    columns: ["orders_short"],
    defaultVisible: true
  },
  {
    id: "orders_long",
    label: "Orders: 60D, 90D, 180D",
    columns: ["orders_long"],
    defaultVisible: false
  },
  {
    id: "pricing",
    label: "Pricing & Buy Box",
    columns: ["pricing"],
    defaultVisible: false
  },
  {
    id: "financial",
    label: "Financial Metrics",
    columns: ["financial"],
    defaultVisible: false
  },
  {
    id: "logistics",
    label: "Logistics & Prep",
    columns: ["logistics"],
    defaultVisible: false
  },
  {
    id: "costs",
    label: "Shipping & Repack Costs",
    columns: ["costs"],
    defaultVisible: false
  },
  {
    id: "marketplace",
    label: "Marketplace Data",
    columns: ["marketplace"],
    defaultVisible: false
  },
  {
    id: "profit",
    label: "Profit (EM, EM%, Markup)",
    columns: ["profit"],
    defaultVisible: true
  },
  {
    id: "reserved",
    label: "Reserved Breakdown",
    columns: ["reserved"],
    defaultVisible: false
  },
  {
    id: "restock_metrics",
    label: "Restock Metrics",
    columns: ["restock_metrics"],
    defaultVisible: false
  },
  {
    id: "order_qty",
    label: "Order Quantity",
    columns: ["order_qty"],
    defaultVisible: true
  },
  {
    id: "notes",
    label: "Notes & Tags",
    columns: ["notes"],
    defaultVisible: false
  }
];
var DEFAULT_VISIBLE_COLUMN_IDS = [
  "select",
  "flag",
  "sku",
  "title",
  "asin",
  "image",
  "supplier",
  "supplierCost",
  "velocity30d",
  "fbaInventory",
  "inboundQty",
  "localQty",
  "daysOfStock",
  "suggestedReorder",
  "weightedVelocity"
];
var CSV_EXPORT_OPTIONS = [
  { id: "all", label: "Download All Data", description: "Export all records (ignores filters)" },
  { id: "visible", label: "Download Current View", description: "Export filtered and visible data" },
  { id: "template", label: "Get Import Template", description: "Download CSV template for bulk import" }
];

// src/constants/products.ts
var DEFAULT_PRODUCTS_COLUMNS = [
  // Always visible
  { id: "select", label: "Select", visible: true, order: 0, width: 40 },
  { id: "image", label: "Image", visible: true, order: 1, width: 60 },
  { id: "product", label: "Product", visible: true, order: 2, width: 300 },
  { id: "status", label: "Status", visible: true, order: 3, width: 96 },
  // Default visible
  { id: "supplier", label: "Supplier", visible: true, order: 4, width: 150 },
  { id: "supplierCost", label: "Cost", visible: true, order: 5, width: 156 },
  { id: "velocity", label: "Velocity (30d)", visible: true, order: 6, width: 116 },
  { id: "revenue30d", label: "Rev 30d", visible: true, order: 7, width: 124 },
  { id: "units30d", label: "Units 30d", visible: true, order: 8, width: 96 },
  { id: "inventory", label: "FBA Qty", visible: true, order: 9, width: 92 },
  { id: "localQty", label: "Local Qty", visible: true, order: 10, width: 108 },
  { id: "leadTimeDays", label: "Lead Time", visible: true, order: 11, width: 96 },
  // Supplier details (default hidden)
  { id: "supplierSku", label: "Supplier SKU", visible: false, order: 12, width: 132 },
  { id: "casePackQty", label: "Case Pack Qty", visible: false, order: 13, width: 104 },
  // Costs (default hidden)
  { id: "inboundShippingCost", label: "Fulfillment Shipping", visible: false, order: 14, width: 136 },
  { id: "prepCost", label: "Prep / Packaging", visible: false, order: 15, width: 108 },
  // Product details (default hidden)
  { id: "type", label: "Type", visible: false, order: 16, width: 112 },
  { id: "condition", label: "Condition", visible: false, order: 17, width: 120 },
  { id: "weight", label: "Weight (lbs)", visible: false, order: 18, width: 96 },
  { id: "dimensionalWeight", label: "Dim. Weight", visible: false, order: 19, width: 116 },
  { id: "sizeTier", label: "Size Tier", visible: false, order: 20, width: 112 },
  { id: "isHazmat", label: "Hazmat", visible: false, order: 21, width: 96 },
  { id: "isOversized", label: "Oversized", visible: false, order: 22, width: 96 },
  // Identifiers (default hidden)
  { id: "upc", label: "UPC", visible: false, order: 23, width: 132 },
  { id: "ean", label: "EAN", visible: false, order: 24, width: 132 },
  // Inventory detail (default hidden)
  { id: "inboundQty", label: "Inbound Qty", visible: false, order: 26, width: 108 },
  { id: "reservedQty", label: "Reserved Qty", visible: false, order: 27, width: 112 },
  { id: "fulfillmentChannel", label: "Fulfillment", visible: false, order: 28, width: 116 },
  { id: "inventoryAge", label: "Inventory Age", visible: false, order: 29, width: 108 },
  // Pricing (default hidden)
  { id: "currentPrice", label: "Price", visible: false, order: 30, width: 104 },
  { id: "buyBoxPrice", label: "Buy Box Price", visible: false, order: 31, width: 120 },
  // Velocity detail (default hidden)
  { id: "velocity7d", label: "Vel. 7d", visible: false, order: 32, width: 96 },
  { id: "velocity60d", label: "Vel. 60d", visible: false, order: 33, width: 100 },
  { id: "velocity90d", label: "Vel. 90d", visible: false, order: 34, width: 100 },
  // Revenue detail (default hidden)
  { id: "revenue7d", label: "Rev 7d", visible: false, order: 35, width: 112 },
  { id: "revenue60d", label: "Rev 60d", visible: false, order: 36, width: 116 },
  { id: "revenue90d", label: "Rev 90d", visible: false, order: 37, width: 116 },
  // Units detail (default hidden)
  { id: "units7d", label: "Units 7d", visible: false, order: 38, width: 96 },
  { id: "units60d", label: "Units 60d", visible: false, order: 39, width: 104 },
  { id: "units90d", label: "Units 90d", visible: false, order: 40, width: 104 },
  // Profitability (default hidden)
  { id: "profit30d", label: "Profit 30d", visible: false, order: 41, width: 116 },
  { id: "profitMargin", label: "Margin %", visible: false, order: 42, width: 108 },
  { id: "roi", label: "ROI", visible: false, order: 43, width: 96 },
  // Settings (default hidden)
  { id: "safetyStockDays", label: "Safety Stock", visible: false, order: 44, width: 112 },
  { id: "prepTimeDays", label: "Prep Time", visible: false, order: 45, width: 100 },
  // Notes (default hidden)
  { id: "notes", label: "Notes", visible: false, order: 46, width: 220 }
];
var PRODUCTS_COLUMN_GROUPS = [
  {
    id: "core",
    label: "Core (Image, Product, Status)",
    columns: ["select", "image", "product", "status"],
    defaultVisible: true
  },
  {
    id: "supplier",
    label: "Supplier Info",
    columns: ["supplier", "supplierCost", "supplierSku", "casePackQty"],
    defaultVisible: true
  },
  {
    id: "costs",
    label: "Costs",
    columns: ["inboundShippingCost", "prepCost"],
    defaultVisible: false
  },
  {
    id: "identifiers",
    label: "Identifiers (UPC, EAN)",
    columns: ["upc", "ean"],
    defaultVisible: false
  },
  {
    id: "product_details",
    label: "Product Details",
    columns: ["type", "condition", "weight", "dimensionalWeight", "sizeTier", "isHazmat", "isOversized"],
    defaultVisible: false
  },
  {
    id: "inventory",
    label: "Inventory",
    columns: ["inventory", "localQty", "inboundQty", "reservedQty", "fulfillmentChannel", "inventoryAge"],
    defaultVisible: true
  },
  {
    id: "pricing",
    label: "Pricing",
    columns: ["currentPrice", "buyBoxPrice"],
    defaultVisible: false
  },
  {
    id: "velocity",
    label: "Velocity",
    columns: ["velocity", "velocity7d", "velocity60d", "velocity90d"],
    defaultVisible: true
  },
  {
    id: "revenue",
    label: "Revenue",
    columns: ["revenue30d", "revenue7d", "revenue60d", "revenue90d"],
    defaultVisible: true
  },
  {
    id: "units",
    label: "Units Sold",
    columns: ["units30d", "units7d", "units60d", "units90d"],
    defaultVisible: false
  },
  {
    id: "profitability",
    label: "Profitability",
    columns: ["profit30d", "profitMargin", "roi"],
    defaultVisible: false
  },
  {
    id: "settings",
    label: "Settings & Logistics",
    columns: ["leadTimeDays", "safetyStockDays", "prepTimeDays"],
    defaultVisible: false
  },
  {
    id: "notes",
    label: "Notes",
    columns: ["notes"],
    defaultVisible: false
  }
];

// src/constants/index.ts
var PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 50,
  MAX_LIMIT: 500
};
var DEFAULT_VELOCITY_WEIGHTS = {
  weight2d: 0,
  weight7d: 0.15,
  weight30d: 0.55,
  weight60d: 0.25,
  weight90d: 0.05,
  weight180d: 0
};
var EXTENDED_VELOCITY_WEIGHTS = {
  weight2d: 0.05,
  weight7d: 0.15,
  weight30d: 0.4,
  weight60d: 0.25,
  weight90d: 0.1,
  weight180d: 0.05
};
var DEFAULT_RESTOCK_PARAMS = {
  safetyStockDays: 14,
  prepTimeDays: 0,
  leadTimeDays: 7,
  targetMinDays: 30,
  targetMaxDays: 45
};
var ALERT_THRESHOLDS = {
  INVENTORY_AGE_WARNING_DAYS: 90,
  LOW_BUY_BOX_SHARE_PERCENT: 50,
  STALE_DATA_HOURS: 24
};
var SYNC_INTERVALS = {
  FULL_SYNC_HOURS: 4,
  INCREMENTAL_SYNC_MINUTES: 15,
  WORKING_SHIPMENT_POLL_SECONDS: 60
};
var PERFORMANCE = {
  DASHBOARD_LOAD_MS: 3e3,
  INVENTORY_TABLE_LOAD_MS: 2e3,
  SEARCH_RESULTS_MS: 5e3,
  STORE_SWITCH_MS: 2e3,
  MAX_SKUS_PER_STORE: 1e4,
  CLIENT_EXPORT_ROW_LIMIT: 5e3
};
var VALIDATION = {
  MAX_STORES_PER_USER: 10,
  MIN_PRICE: 0,
  MAX_SKU_LENGTH: 40,
  MAX_TITLE_LENGTH: 500,
  MAX_NOTES_LENGTH: 2e3
};
var API_ROUTES = {
  // Auth
  AUTH: "/api/auth",
  // Stores
  STORES: "/api/stores",
  STORE: (id) => `/api/stores/${id}`,
  // Products
  PRODUCTS: "/api/products",
  PRODUCT: (id) => `/api/products/${id}`,
  PRODUCTS_IMPORT: "/api/products/import",
  PRODUCTS_EXPORT: "/api/products/export",
  // Suppliers
  SUPPLIERS: "/api/suppliers",
  SUPPLIER: (id) => `/api/suppliers/${id}`,
  // Inventory
  INVENTORY: "/api/inventory",
  INVENTORY_EXPORT: "/api/inventory/export",
  RESTOCK_SUGGESTIONS: "/api/inventory/restock-suggestions",
  // Purchase Orders
  PURCHASE_ORDERS: "/api/purchase-orders",
  PURCHASE_ORDER: (id) => `/api/purchase-orders/${id}`,
  PURCHASE_ORDER_RECEIVE: (id) => `/api/purchase-orders/${id}/receive`,
  // Shipment Plans
  SHIPMENT_PLANS: "/api/shipment-plans",
  SHIPMENT_PLAN: (id) => `/api/shipment-plans/${id}`,
  // Shipments
  SHIPMENTS: "/api/shipments",
  SHIPMENT: (id) => `/api/shipments/${id}`,
  SHIPMENT_PACK: (id) => `/api/shipments/${id}/pack`,
  SHIPMENT_LABELS: (id) => `/api/shipments/${id}/labels`,
  SHIPMENTS_EXPORT: "/api/shipments/export",
  // Listings
  LISTINGS: "/api/listings",
  LISTING: (id) => `/api/listings/${id}`,
  LISTINGS_EXPORT: "/api/listings/export",
  // Dashboard
  DASHBOARD_KPIS: "/api/dashboard/kpis",
  DASHBOARD_INSIGHTS: "/api/dashboard/insights",
  DASHBOARD_TOP_PRODUCTS: "/api/dashboard/top-products",
  DASHBOARD_SUPPLIER_RESTOCK: "/api/dashboard/supplier-restock",
  // Sync
  SYNC_STATUS: "/api/sync/status",
  SYNC_TRIGGER: "/api/sync/trigger",
  SYNC_LOGS: "/api/sync/logs",
  // Alerts
  ALERTS: "/api/alerts",
  ALERT_DISMISS: (id) => `/api/alerts/${id}/dismiss`
};
var HEADERS = {
  STORE_ID: "x-store-id",
  ACCOUNT_ID: "x-account-id",
  AUTHORIZATION: "Authorization",
  CONTENT_TYPE: "Content-Type"
};
var ERROR_CODES = {
  DEVICE_LIMIT_EXCEEDED: "DEVICE_LIMIT_EXCEEDED",
  DEVICE_VERIFICATION_UNAVAILABLE: "DEVICE_VERIFICATION_UNAVAILABLE",
  // Authentication
  UNAUTHORIZED: "UNAUTHORIZED",
  FORBIDDEN: "FORBIDDEN",
  INVALID_TOKEN: "INVALID_TOKEN",
  // Validation
  VALIDATION_ERROR: "VALIDATION_ERROR",
  INVALID_INPUT: "INVALID_INPUT",
  MISSING_REQUIRED_FIELD: "MISSING_REQUIRED_FIELD",
  // Resources
  NOT_FOUND: "NOT_FOUND",
  ALREADY_EXISTS: "ALREADY_EXISTS",
  CONFLICT: "CONFLICT",
  // Master SKU purchase-order allocation
  MASTER_SKU_ALLOCATION_REQUIRED: "MASTER_SKU_ALLOCATION_REQUIRED",
  PO_ALLOCATION_INELIGIBLE: "PO_ALLOCATION_INELIGIBLE",
  PO_IMPORTED_SHIPMENT_RECONCILIATION_REQUIRED: "PO_IMPORTED_SHIPMENT_RECONCILIATION_REQUIRED",
  ALLOCATION_PREVIEW_STALE: "ALLOCATION_PREVIEW_STALE",
  // Store
  STORE_NOT_FOUND: "STORE_NOT_FOUND",
  STORE_LIMIT_REACHED: "STORE_LIMIT_REACHED",
  STORE_ID_REQUIRED: "STORE_ID_REQUIRED",
  // Research scanner billing and quota
  RESEARCH_SCANNER_SUBSCRIPTION_REQUIRED: "RESEARCH_SCANNER_SUBSCRIPTION_REQUIRED",
  RESEARCH_SCANNER_MONTHLY_LIMIT: "RESEARCH_SCANNER_MONTHLY_LIMIT",
  RESEARCH_SCANNER_OPERATION_LIMIT: "RESEARCH_SCANNER_OPERATION_LIMIT",
  RESEARCH_SCANNER_DISABLED: "RESEARCH_SCANNER_DISABLED",
  // Sync
  SYNC_IN_PROGRESS: "SYNC_IN_PROGRESS",
  SYNC_FAILED: "SYNC_FAILED",
  AMAZON_API_ERROR: "AMAZON_API_ERROR",
  // Shipment finalization
  LEGACY_SYNC_DISABLED: "LEGACY_SYNC_DISABLED",
  PRODUCTION_VOID_UNSUPPORTED: "PRODUCTION_VOID_UNSUPPORTED",
  AMAZON_CANCEL_FAILED: "AMAZON_CANCEL_FAILED",
  AMAZON_BOX_ID_RECONCILIATION_FAILED: "AMAZON_BOX_ID_RECONCILIATION_FAILED",
  ...TRANSPORTATION_ERROR_CODES,
  // General
  INTERNAL_ERROR: "INTERNAL_ERROR",
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE",
  RATE_LIMITED: "RATE_LIMITED"
};
var MARKETPLACE_DETAILS = {
  US: { name: "United States", region: "NA", currency: "USD", domain: "amazon.com" },
  CA: { name: "Canada", region: "NA", currency: "CAD", domain: "amazon.ca" },
  MX: { name: "Mexico", region: "NA", currency: "MXN", domain: "amazon.com.mx" },
  BR: { name: "Brazil", region: "NA", currency: "BRL", domain: "amazon.com.br" },
  UK: { name: "United Kingdom", region: "EU", currency: "GBP", domain: "amazon.co.uk" },
  DE: { name: "Germany", region: "EU", currency: "EUR", domain: "amazon.de" },
  FR: { name: "France", region: "EU", currency: "EUR", domain: "amazon.fr" },
  IT: { name: "Italy", region: "EU", currency: "EUR", domain: "amazon.it" },
  ES: { name: "Spain", region: "EU", currency: "EUR", domain: "amazon.es" },
  NL: { name: "Netherlands", region: "EU", currency: "EUR", domain: "amazon.nl" },
  SE: { name: "Sweden", region: "EU", currency: "SEK", domain: "amazon.se" },
  PL: { name: "Poland", region: "EU", currency: "PLN", domain: "amazon.pl" },
  TR: { name: "Turkey", region: "EU", currency: "TRY", domain: "amazon.com.tr" },
  AE: { name: "United Arab Emirates", region: "EU", currency: "AED", domain: "amazon.ae" },
  SA: { name: "Saudi Arabia", region: "EU", currency: "SAR", domain: "amazon.sa" },
  IN: { name: "India", region: "EU", currency: "INR", domain: "amazon.in" },
  SG: { name: "Singapore", region: "FE", currency: "SGD", domain: "amazon.sg" },
  AU: { name: "Australia", region: "FE", currency: "AUD", domain: "amazon.com.au" },
  JP: { name: "Japan", region: "FE", currency: "JPY", domain: "amazon.co.jp" }
};
var LISTING_FLAG_COLORS = {
  RED: { bg: "bg-red-500", text: "text-white", label: "Urgent" },
  ORANGE: { bg: "bg-orange-500", text: "text-white", label: "Warning" },
  YELLOW: { bg: "bg-yellow-500", text: "text-black", label: "Caution" },
  PURPLE: { bg: "bg-purple-500", text: "text-white", label: "Review" },
  BLUE: { bg: "bg-blue-500", text: "text-white", label: "Info" },
  NONE: { bg: "bg-gray-200", text: "text-gray-600", label: "None" }
};
var DEFAULT_RESTOCK_COLUMNS = [
  { id: "select", label: "Select", visible: true, order: 0, width: 40, sticky: true },
  { id: "image", label: "Image", visible: true, order: 1, width: 56, sticky: true },
  { id: "product", label: "Product", visible: true, order: 2, width: 300, sticky: true },
  { id: "identifiers", label: "Identifiers", visible: false, order: 3, width: 132 },
  { id: "workflow", label: "Workflow", visible: true, order: 4, width: 112 },
  { id: "supplier", label: "Supplier", visible: true, order: 5, width: 220 },
  { id: "inventory_summary", label: "Inventory", visible: true, order: 6, width: 150 },
  { id: "restock", label: "Restock", visible: true, order: 7, width: 180 },
  { id: "velocity_short", label: "2D/7D/30D velocity", visible: true, order: 8, width: 140 },
  { id: "velocity_long", label: "60D/90D/180D velocity", visible: false, order: 9, width: 140 },
  { id: "orders_short", label: "Sales (units/orders)", visible: true, order: 10, width: 160 },
  { id: "orders_long", label: "Long sales (units/orders)", visible: false, order: 11, width: 160 },
  { id: "pricing", label: "Pricing", visible: false, order: 12, width: 160 },
  { id: "financial", label: "Financial", visible: false, order: 13, width: 150 },
  { id: "logistics", label: "Logistics", visible: false, order: 14, width: 170 },
  { id: "costs", label: "Costs", visible: false, order: 15, width: 180 },
  { id: "marketplace", label: "Marketplace", visible: false, order: 16, width: 190 },
  { id: "profit", label: "Profit", visible: true, order: 17, width: 150 },
  { id: "reserved", label: "Reserved", visible: false, order: 18, width: 140 },
  { id: "restock_metrics", label: "Restock Metrics", visible: false, order: 19, width: 160 },
  { id: "order_qty", label: "Order Qty", visible: true, order: 20, width: 120 },
  { id: "notes", label: "Notes", visible: false, order: 21, width: 220 }
];
var RESTOCK_URGENCY_LEVELS = {
  OUT_OF_STOCK: { threshold: 0, color: "red", label: "Out of Stock" },
  CRITICAL: { threshold: 7, color: "red", label: "Critical (<7 days)" },
  LOW: { threshold: 14, color: "orange", label: "Low Stock (<14 days)" },
  WARNING: { threshold: 30, color: "yellow", label: "Warning (<30 days)" },
  HEALTHY: { threshold: Infinity, color: "green", label: "Healthy" }
};

export {
  TRANSPORTATION_ERROR_CODES,
  PERMISSIONS,
  ALL_PERMISSIONS,
  PERMISSION_DETAILS,
  PERMISSION_GROUPS,
  ROLE_SLUGS,
  PRESET_ROLE_DEFINITIONS,
  isPermission,
  DASHBOARD_WIDGET_IDS,
  DASHBOARD_WIDGET_PERMISSION_REQUIREMENTS,
  canAccessDashboardWidget,
  getAvailableDashboardWidgets,
  DASHBOARD_SCHEMA_VERSION,
  ZENO_CAROUSEL_SUGGESTIONS,
  RESTOCK_STATUS,
  RESTOCK_STATUS_LABELS,
  RESTOCK_STATUS_COLORS,
  RESTOCK_FILTER_CATEGORIES,
  RESTOCK_FILTERS,
  RESTOCK_COLUMN_GROUPS,
  DEFAULT_VISIBLE_COLUMN_IDS,
  CSV_EXPORT_OPTIONS,
  DEFAULT_PRODUCTS_COLUMNS,
  PRODUCTS_COLUMN_GROUPS,
  PAGINATION,
  DEFAULT_VELOCITY_WEIGHTS,
  EXTENDED_VELOCITY_WEIGHTS,
  DEFAULT_RESTOCK_PARAMS,
  ALERT_THRESHOLDS,
  SYNC_INTERVALS,
  PERFORMANCE,
  VALIDATION,
  API_ROUTES,
  HEADERS,
  ERROR_CODES,
  MARKETPLACE_DETAILS,
  LISTING_FLAG_COLORS,
  DEFAULT_RESTOCK_COLUMNS,
  RESTOCK_URGENCY_LEVELS
};
